import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ScrollView,
  Pressable,
  ActivityIndicator,
  Platform,
  Keyboard,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { KeyboardSafeView } from '@/components/common/KeyboardSafeView';
import { KeyboardDismissWrapper } from '@/components/common/KeyboardDismissWrapper';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useResponsive } from '@/hooks/common/useResponsive';

const CloseIcon = SVG_COMPONENTS.CANCEL_X;

interface StepWizardModalProps {
  visible: boolean;
  onClose: () => void;
  currentStepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
  isNextEnabled: boolean;
  isSubmitting?: boolean;
  isLastStep: boolean;
  nextLabel?: string;
  submitLabel?: string;
  children: React.ReactNode;
  renderFooter?: () => React.ReactNode;
}

export const StepWizardModal: React.FC<StepWizardModalProps> = ({
  visible,
  onClose,
  currentStepIndex,
  totalSteps,
  onNext,
  onBack,
  isNextEnabled,
  isSubmitting = false,
  isLastStep,
  nextLabel = 'Next',
  submitLabel = 'Submit',
  children,
  renderFooter,
}) => {
  const insets = useSafeAreaInsets();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';

  // On Android, KeyboardAvoidingView doesn't work reliably inside Modal.
  // Track keyboard height manually and apply paddingBottom instead.
  const isAndroid = Platform.OS === 'android';
  const [androidKeyboardHeight, setAndroidKeyboardHeight] = useState(0);

  useEffect(() => {
    if (!isAndroid || isDesktop) return;

    const showSub = Keyboard.addListener('keyboardDidShow', (e) => {
      setAndroidKeyboardHeight(e.endCoordinates.height);
    });
    const hideSub = Keyboard.addListener('keyboardDidHide', () => {
      setAndroidKeyboardHeight(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, [isAndroid, isDesktop]);

  const isFirstStep = currentStepIndex === 0;
  const progressPercent = currentStepIndex === 0 ? 0 : (currentStepIndex / (totalSteps - 1)) * 100;

  const defaultFooter = () => (
    <View style={stepWizardStyles.footer}>
      <TouchableOpacity
        style={[
          stepWizardStyles.secondaryButton,
          isFirstStep && stepWizardStyles.buttonHidden,
        ]}
        onPress={onBack}
        disabled={isFirstStep}
        activeOpacity={0.8}
      >
        <Text style={stepWizardStyles.secondaryButtonText}>Back</Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[
          stepWizardStyles.primaryButton,
          (!isNextEnabled || isSubmitting) && stepWizardStyles.primaryButtonDisabled,
        ]}
        onPress={onNext}
        disabled={!isNextEnabled || isSubmitting}
        activeOpacity={0.8}
      >
        {isSubmitting ? (
          <ActivityIndicator size="small" color={MARKETPLACE_COLORS.buttonDisabledText} />
        ) : (
          <Text style={[
            stepWizardStyles.primaryButtonText,
            !isNextEnabled && stepWizardStyles.primaryButtonTextDisabled,
          ]}>
            {isLastStep ? submitLabel : nextLabel}
          </Text>
        )}
      </TouchableOpacity>
    </View>
  );

  const footerContent = renderFooter ? renderFooter() : defaultFooter();

  const innerContent = (
    <KeyboardDismissWrapper style={styles.innerContainer}>
      <View style={isDesktop ? [styles.header, styles.headerDesktop] : styles.header}>
        <View style={styles.progressBarContainer}>
          <View style={styles.progressBarBackground}>
            <View
              style={[
                styles.progressBarFill,
                { width: `${progressPercent}%` },
              ]}
            />
          </View>
        </View>

        <TouchableOpacity
          style={styles.closeButton}
          onPress={onClose}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <CloseIcon width={16} height={16} color={MARKETPLACE_COLORS.textMuted} />
        </TouchableOpacity>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={isDesktop ? [styles.contentContainer, styles.contentContainerDesktop] : styles.contentContainer}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        bounces={false}
      >
        {children}
      </ScrollView>

      <View style={
        isDesktop
          ? [styles.footerContainer, styles.footerContainerDesktop, { paddingBottom: 24 }]
          : [styles.footerContainer, { paddingBottom: Math.max(insets.bottom, 24) }]
      }>
        {footerContent}
      </View>
    </KeyboardDismissWrapper>
  );

  const containerStyle = isDesktop
    ? StyleSheet.flatten([styles.container, styles.containerDesktop])
    : styles.container;

  const modalContent = isAndroid && !isDesktop ? (
    <View style={[
      containerStyle,
      { paddingTop: insets.top },
      androidKeyboardHeight > 0 && { paddingBottom: androidKeyboardHeight + 8 },
    ]}>
      {innerContent}
    </View>
  ) : (
    <KeyboardSafeView isModal style={containerStyle}>
      {innerContent}
    </KeyboardSafeView>
  );

  if (isDesktop) {
    return (
      <Modal
        visible={visible}
        animationType="fade"
        transparent={true}
        onRequestClose={onClose}
      >
        <View style={styles.desktopOverlay}>
          <Pressable style={styles.desktopBackdrop} onPress={onClose} />
          <View style={styles.desktopDialog}>
            {modalContent}
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      statusBarTranslucent
      onRequestClose={onClose}
    >
      {modalContent}
    </Modal>
  );
};

export const stepWizardStyles = StyleSheet.create({
  footer: {
    flexDirection: 'row',
    gap: 20,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  secondaryButton: {
    flex: 1,
    height: 34,
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.textPrimary,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 18,
  },
  primaryButton: {
    flex: 1,
    height: 34,
    backgroundColor: MARKETPLACE_COLORS.accentRed,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonDisabled: {
    backgroundColor: MARKETPLACE_COLORS.buttonDisabled,
  },
  primaryButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.black,
    lineHeight: 18,
  },
  primaryButtonTextDisabled: {
    color: MARKETPLACE_COLORS.buttonDisabledText,
  },
  buttonHidden: {
    opacity: 0,
  },
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: MARKETPLACE_COLORS.modalBackground,
  },
  containerDesktop: {
    borderRadius: 20,
    overflow: 'hidden',
  },
  innerContainer: {
    flex: 1,
  },
  desktopOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  desktopBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  desktopDialog: {
    width: '90%',
    maxWidth: 560,
    maxHeight: '85%',
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: MARKETPLACE_COLORS.dividerGray,
    gap: 20,
  },
  headerDesktop: {
    paddingHorizontal: 32,
    paddingVertical: 24,
  },
  progressBarContainer: {
    flex: 1,
  },
  progressBarBackground: {
    height: 8,
    backgroundColor: MARKETPLACE_COLORS.progressBarBackground,
    borderRadius: 8,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: MARKETPLACE_COLORS.accentBlueSolid,
    borderRadius: 8,
  },
  closeButton: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    paddingBottom: 20,
  },
  contentContainer: {
    flexGrow: 1,
  },
  contentContainerDesktop: {
    paddingHorizontal: 8,
  },
  footerContainer: {
    borderTopWidth: 1,
    borderTopColor: MARKETPLACE_COLORS.dividerGray,
    backgroundColor: MARKETPLACE_COLORS.modalBackground,
  },
  footerContainerDesktop: {
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
});
